import {
  PRODUCT_IMAGERY_DEFAULT_LABELS_BY_LEVEL,
  PROPERTIES_ERRORS,
} from './constants';

let cachedPropertyMap = null;
let cachedPropertiesLength = 0;
let cachedImageMap = null;

const createPropertyMap = (properties) => {
  if (cachedPropertyMap && cachedPropertiesLength === properties.length) {
    return cachedPropertyMap;
  }

  const mappingErrors = PROPERTIES_ERRORS.map((item) => ({
    ...item,
    packageLevel: 'Unit',
  }));

  const map = new Map();
  const mergedProperties = [...properties, ...mappingErrors];

  for (const prop of mergedProperties) {
    const key = prop?.fieldFullPath?.toLowerCase();
    if (key) {
      map.set(key, prop);
    }
  }

  cachedPropertyMap = map;
  cachedPropertiesLength = properties.length;
  return map;
};

const createImageMap = () => {
  if (cachedImageMap) return cachedImageMap;

  const map = new Map();

  for (const [packageLevel, imageLabels] of Object.entries(
    PRODUCT_IMAGERY_DEFAULT_LABELS_BY_LEVEL
  )) {
    const levelMap = new Map();

    for (const [constantId, displayLabel] of Object.entries(imageLabels)) {
      const imageData = {
        fieldDisplayName: displayLabel,
        displayName: displayLabel,
        fieldFullPath: constantId,
        fullPathDisplayName: displayLabel,
        packageLevel,
      };

      levelMap.set(constantId.toLowerCase(), imageData);
    }

    map.set(packageLevel, levelMap);
  }

  cachedImageMap = map;
  return map;
};

const getImageData = (fieldName, packageLevel = 'Unit') => {
  const imageMap = createImageMap();
  const levelMap = imageMap.get(packageLevel);

  if (!levelMap) {
    const unitMap = imageMap.get('Unit');
    return unitMap?.get(fieldName.toLowerCase()) || null;
  }

  return levelMap.get(fieldName.toLowerCase()) || null;
};

export const handleGetDisplayName = (
  errors = [],
  properties = [],
  packageLevel = 'Unit'
) => {
  if (!Array.isArray(errors) || !Array.isArray(properties)) return [];

  const propertyMap = createPropertyMap(properties);
  const specialFields = new Set(['upc', 'c1n1']);
  const result = [];

  for (const item of errors) {
    const fieldName = item?.fieldName;
    if (!fieldName) continue;

    const fieldNameLower = fieldName.toLowerCase();

    if (specialFields.has(fieldNameLower)) continue;

    let fieldData = propertyMap.get(fieldNameLower);

    if (!fieldData) {
      fieldData = getImageData(fieldName, packageLevel);
    }

    result.push({
      ...fieldData,
      ...item,
      fieldName,
      name: fieldData?.displayName || fieldName,
    });
  }

  return result;
};

export const mergeErrors = (
  errors = [],
  minimumRequirementErrorFields = []
) => {
  if (!Array.isArray(errors)) errors = [];
  if (!Array.isArray(minimumRequirementErrorFields))
    minimumRequirementErrorFields = [];

  const errorFieldNames = new Set(
    errors.map((e) => e?.fieldName?.toLowerCase()).filter(Boolean)
  );

  const result = [...errors];

  for (const field of minimumRequirementErrorFields) {
    if (field && !errorFieldNames.has(field.toLowerCase())) {
      result.push({
        fieldName: field,
        errorMessage: null,
      });
    }
  }

  return result;
};

export const generateErrorFieldList = (data = [], properties = []) => {
  if (!Array.isArray(data) || !data.length) return [];

  const propertyMap = createPropertyMap(properties);
  const specialFields = new Set(['upc', 'c1n1']);
  const resultMap = new Map();

  for (const product of data) {
    let errors = Array.isArray(product?.errors) ? product.errors : [];
    errors = errors.filter((error) => error.isWarning === false);
    const minimumFields = Array.isArray(product?.minimumRequirementErrorFields)
      ? product.minimumRequirementErrorFields
      : [];
    const packageLevel = product?.packageLevel || 'Unit';

    const errorFieldNames = new Set(
      errors.map((e) => e?.fieldName?.toLowerCase()).filter(Boolean)
    );

    const allErrors = [...errors];
    for (const field of minimumFields) {
      if (field && !errorFieldNames.has(field.toLowerCase())) {
        allErrors.push({ fieldName: field, errorMessage: null });
      }
    }

    for (const item of allErrors) {
      const fieldName = item?.fieldName;
      if (!fieldName) continue;

      const fieldNameLower = fieldName.toLowerCase();
      if (specialFields.has(fieldNameLower)) continue;

      let fieldData = propertyMap.get(fieldNameLower);

      if (!fieldData) {
        fieldData = getImageData(fieldName, packageLevel);
      }

      const key = fieldData?.fieldFullPath || fieldName;

      if (
        !resultMap.has(key) ||
        (item.errorMessage && !resultMap.get(key).errorMessage)
      ) {
        resultMap.set(key, {
          ...fieldData,
          ...item,
          fieldName,
          name: fieldData?.displayName || fieldName,
        });
      }
    }
  }

  return Array.from(resultMap.values());
};

export const createErrorMapByProduct = (data) => {
  if (!data) return new Map();

  const map = new Map();
  for (const product of data) {
    const productKey = `${product.upc || product.productId}_${product.packageLevel}`;
    const errorMap = new Map();

    const allErrors = [
      ...(product.errors || []),
      ...(product.minimumRequirementErrorFields || []).map((field) => ({
        fieldName: field,
        errorMessage: null,
      })),
    ];

    for (const error of allErrors) {
      const fieldKey = error?.fieldName?.toLowerCase();
      if (fieldKey) {
        errorMap.set(fieldKey, error);
      }
    }

    map.set(productKey, {
      errorMap,
      packageLevel: product.packageLevel,
    });
  }

  return map;
};
